import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import {
  ZfBox,
  ZfDivider,
  ZfFormField,
  ZfIcon,
  ZfInput,
  ZfLoadingSpinner,
  ZfPill,
  ZfPopover,
  ZfSelectOption,
  ZfStack,
  ZfTypography,
} from '@zafire/react';
import { DropdownOption } from 'components/Common/ValidatedDropdown';
import { useFormikContext } from 'formik';
import { GlobalState } from 'reducers/stateTypes';
import ContactNote from 'types/ContactNote';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import { useGetRelatedBrs } from 'hooks/useGetRelatedBrs';
import { useGetPartnerDetails } from 'hooks/useGetPartnerDetails';
import { useGetClientRepresentative } from 'hooks/useGetClientRepresentative';
import { format, parse, isValid } from 'date-fns';
import { useFetchBRBusinessRelationship } from 'hooks/useFetchBRBusinessRelationship';
import { BusinessRelationshipMultipleItem, BusinessRelationshipResponse } from 'types/BusinessRelationship';
import {
  employeeSearchName,
  formatBrLabel,
  matchesContainsSearch,
  normalizeSearchText,
  toBrOption,
} from 'utils/contactNoteUtil';
import { useGetMultiplePartnerDetails } from 'hooks/useGetMultiplePartnerDetails';
import { DeserialisedContactNote, isBRFieldMandatoryCheck } from 'utils/noteDialogUtil';
import { EnvConfig } from 'config/envConfig';
import { User } from 'types/User';

import { SelectOption } from './data/ContactNoteDialogOptions';
import BRPartnerDetailTable from './BRPartnerDetailTable';

export type RelatedByDropdownItem = { busRelUid: string; relatedBrName: string };
const DROPDOWN_HEIGHT = 32;
const MAX_BR_OPTIONS = 50;

const GROWING_PILL_STYLE = { '--zf-pill-min-height-medium': 'auto' } as React.CSSProperties;

//isPartnerBR is true, relatedBR is depending on techId
const AttendeesBr = ({
  nonEditable = false,
  edit = false,
  isPartnerBR = true,
  isFederated = false,
  brUId = '',
  partnerUId = '',
}: {
  nonEditable: boolean;
  edit: boolean;
  isPartnerBR?: boolean;
  isFederated?: boolean;
  brUId?: string;
  partnerUId?: string;
}) => {
  const { t } = useTranslation();
  const { getFieldProps, values, touched, errors, setFieldValue } = useFormikContext<DeserialisedContactNote>();

  const params = useParams<{ techId: string }>();
  const techId = isFederated ? brUId || partnerUId : params.techId;

  const usedInBrFromStore = useSelector(selectIsUsedInBr);
  const usedInBr = isFederated ? brUId !== null : usedInBrFromStore;

  const shownNote: ContactNote | null = useSelector((state: GlobalState) => state.contactNoteList.shownNote);
  const [employeeSuggestion, setEmployeeSuggestion] = useState('');
  const [relatedBrSuggestion, setRelatedBrSuggestion] = useState('');
  const deferredRelatedBrSuggestion = useDeferredValue(relatedBrSuggestion);
  const [clientRepresentativeSuggestion, setClientRepresentativeSuggestion] = useState('');
  const [debouncedLabel, setDebouncedLabel] = useState('');
  const countryOptions = useSelector((state: GlobalState) => state.resources.countries);
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const [clientRepresentativeList, setClientRepresentativeList] = useState<SelectOption[]>([]);
  const [relatedBrList, setRelatedBrList] = useState<SelectOption[]>([]);

  const myTravelNonEditableField = useMemo(() => {
    return values?.source === 'MYTRAVEL' && edit;
  }, [values?.source, edit]);

  //For Business Relationship when inBR\
  const { data: brDetails } = useFetchBRBusinessRelationship(techId || '', usedInBr);
  const {
    data: clientRepDetails,
    isFetching: clientRepDetailsLoading,
    isSuccess: clientRepDetailsSuccess,
    isError: clientRepDetailsFail,
  } = useGetClientRepresentative(debouncedLabel);
  useEffect(() => {
    if (techId && usedInBr && values.relatedBRs.length === 0) {
      setFieldValue('relatedBRs', [techId]);
    }
  }, [techId, usedInBr, values.relatedBRs]);

  const { data: partnerDetails } = useGetPartnerDetails(techId || '', usedInBr);

  useEffect(() => {
    if (!usedInBr && partnerDetails && !values.partner.fullName) {
      let fullName, globalPartnerId, type, partnerUid;
      if (partnerDetails.partnerStructureType === 'INDIVIDUAL_PARTNER') {
        const selectedIndividualPartner = partnerDetails.individualPartner.individualPartnerNames.find(
          (item) => item.mainName,
        );
        fullName = `${selectedIndividualPartner?.firstName} ${selectedIndividualPartner?.name}`;
        globalPartnerId = partnerDetails.individualPartner.partnerId;
        type = 'PI';
        partnerUid = partnerDetails.individualPartner.partnerUId;
      } else if (partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER') {
        const selectedLegalEntity = partnerDetails.legalPartner.legalStructurePartnerNames.find(
          (item) => item.mainName,
        );
        fullName = selectedLegalEntity?.name;
        globalPartnerId = partnerDetails.legalPartner.partnerId;
        type = 'LE';
        partnerUid = partnerDetails.legalPartner.partnerUId;
      }
      setFieldValue('partner', {
        ...values.partner,
        globalPartnerId,
        fullName: fullName,
      });
      if (!edit) {
        setFieldValue('relatedPartners', [{ value: partnerUid, type: type }]);
      }
      setClientRepresentativeList([{ value: partnerUid || '', label: `${fullName} (${globalPartnerId})`, type }]);
    }
  }, [usedInBr, edit, values.partner, partnerDetails]);

  const showSelectedClientRep = useMemo(() => {
    if (partnerDetails) {
      return partnerDetails.partnerStructureType === 'LEGAL_ENTITY_PARTNER';
    }
    return false;
  }, [partnerDetails]);

  const selectedClientRepLabel = useMemo(() => {
    if (showSelectedClientRep) {
      if (edit && shownNote) {
        const selectedItem = shownNote.relatedPartners.find((item) => item.partnerUId === techId);
        return `${selectedItem?.fullName} (${selectedItem?.globalPartnerId})`;
      }
      if (!edit && values.partner) {
        return `${values?.partner?.fullName} (${values?.partner?.globalPartnerId})`;
      }
    }
    return '';
  }, [showSelectedClientRep, edit, shownNote, values.partner, techId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedLabel(clientRepresentativeSuggestion);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [clientRepresentativeSuggestion]);

  const includePartnerIntoClientRep = useMemo(() => {
    return !edit && !showSelectedClientRep && !usedInBr && partnerDetails;
  }, [showSelectedClientRep, usedInBr, partnerDetails, edit]);

  const relatedPartnersValue = useMemo(() => {
    if (showSelectedClientRep) {
      return values.relatedPartners.map((item) => item?.value).filter((item) => item !== techId);
    }
    return values.relatedPartners.map((item) => item.value);
  }, [values.relatedPartners, values?.partner?.partnerUId, showSelectedClientRep, techId]);

  const { data: multiplePartnerDetails } = useGetMultiplePartnerDetails(relatedPartnersValue, !isPartnerBR);

  const isPartner = useMemo(() => {
    if (multiplePartnerDetails || partnerDetails) {
      return isBRFieldMandatoryCheck(multiplePartnerDetails || partnerDetails);
    }

    return false;
  }, [partnerDetails, multiplePartnerDetails]);

  //For Business Relationship when !inBR
  const {
    data: relatedBrDetails,
    isFetching: relatedBrloading,
    isSuccess: relatedBrSuccess,
  } = useGetRelatedBrs(isPartnerBR ? techId || '' : relatedPartnersValue, usedInBr);

  const clientRepList = useMemo(() => {
    if (clientRepDetailsSuccess && !!clientRepDetails) {
      return clientRepDetails.hits.map((item) => {
        let doLabel = '',
          domLabel = '',
          formattedDate = '';
        if (item.dob) {
          const parsedDate = parse(item.dob, 'yyyyMMdd', new Date());
          if (isValid(parsedDate)) {
            formattedDate = format(parsedDate, 'dd.MM.yyyy');
          }
        }

        const country = countryOptions.find((obj) => obj.value === item.mainDomicileCountry);
        if (item.type === 'PI') {
          doLabel = `DOB: ${formattedDate || 'null'}`;

          domLabel = `Domicile: ${country?.label || 'null'}`;
        } else {
          doLabel = `DOI: ${formattedDate || 'null'}`;
          domLabel = `Incorporation: ${country?.label || 'null'}`;
        }
        return {
          label: `${item.name} (${item.bizKey}) | ${doLabel} | ${domLabel}`,
          value: item.uuid,
        };
      });
    }
    return [];
  }, [countryOptions, clientRepDetails, clientRepDetailsSuccess]);

  const clientRepFilteredOptions = useMemo(() => {
    const list = clientRepList?.length > 50 ? clientRepList.slice(0, 50) : clientRepList;
    return list;
  }, [clientRepList, clientRepresentativeSuggestion]);

  const employeeOptions = useSelector((state: GlobalState) => state.resources.employeeOptions);

  const employeeOptionsWithInactiveUsers: DropdownOption[] = useMemo(() => {
    const employeeOptionValues = new Set(employeeOptions.map((e) => e.value));
    const isUserInEmployees = employeeOptions.find((item) => item.value === user.username);
    if (isUserInEmployees) {
      // Merge userInfo, employeeOptions, and filtered additionalAttendees
      return [
        ...employeeOptions,
        ...(values.additionalAttendees
          ?.filter((value: string) => {
            return !employeeOptionValues.has(value);
          })
          ?.map((user: string) => {
            const selectedAdditionalAttendee = shownNote?.additionalAttendees.find((item) => item.userId === user);
            if (selectedAdditionalAttendee) {
              return { label: `${selectedAdditionalAttendee.fullName || 'N/A'} (${user})`, value: user };
            }
            return { label: `N/A (${user})`, value: user };
          }) ?? []),
      ];
    } else {
      const userInfo = [{ value: user.username, label: 'N/A (' + user.username + ')' }];
      const userInfoValues = new Set(userInfo.map((u) => u.value));
      return userInfo.concat(
        employeeOptions,
        values.additionalAttendees
          ?.filter((value: string) => {
            return !userInfoValues.has(value) && !employeeOptionValues.has(value);
          })
          ?.map((user: string) => {
            const selectedAdditionalAttendee = shownNote?.additionalAttendees.find((item) => item.userId === user);
            if (selectedAdditionalAttendee) {
              return { label: `${selectedAdditionalAttendee.fullName || 'N/A'} (${user})`, value: user };
            }
            return { label: `N/A (${user})`, value: user };
          }) ?? [],
      );
    }
  }, [employeeOptions, values.additionalAttendees, shownNote?.additionalAttendees, user]);

  // normalized once per list; the `*` wildcards anchor to the name and the user id, not to the "(user id)" label end
  const searchableEmployees = useMemo(
    () =>
      employeeOptionsWithInactiveUsers.map((option) => ({
        option,
        label: normalizeSearchText(option.label ?? ''),
        anchors: [normalizeSearchText(employeeSearchName(option.label ?? '')), normalizeSearchText(option.value)],
      })),
    [employeeOptionsWithInactiveUsers],
  );

  const employeeFilteredOptions = useMemo(() => {
    const suggestion = normalizeSearchText(employeeSuggestion);
    const matches: DropdownOption[] = [];
    for (const { option, ...searchable } of searchableEmployees) {
      if (matchesContainsSearch(suggestion, searchable)) {
        matches.push(option);
        if (matches.length === 50) {
          break;
        }
      }
    }
    return matches;
  }, [searchableEmployees, employeeSuggestion]);

  const relatedBRs = useMemo(() => {
    if (!usedInBr && relatedBrSuccess && !!relatedBrDetails) {
      if (isPartnerBR) {
        return (relatedBrDetails as BusinessRelationshipResponse[]).map(toBrOption);
      } else {
        const groupedUniqueBrs = Array.from(
          new Map(
            (relatedBrDetails as BusinessRelationshipMultipleItem[]).flatMap((item) =>
              (item.relatedBrs ?? []).map((br) => [br.brUid, br] as const),
            ),
          ).values(),
        );
        return groupedUniqueBrs.map(toBrOption);
      }
    }
    return [];
  }, [relatedBrDetails, relatedBrSuccess, usedInBr, isPartnerBR]);

  const searchableBrs = useMemo(
    () =>
      (relatedBRs ?? []).map((option) => ({
        option,
        label: normalizeSearchText(option.label),
        anchors: [normalizeSearchText(option.brName ?? ''), normalizeSearchText(option.brNumber ?? '')],
      })),
    [relatedBRs],
  );

  const { options: relatedBRFilteredOption, hasMore: hasMoreRelatedBrOptions } = useMemo(() => {
    const suggestion = normalizeSearchText(deferredRelatedBrSuggestion);
    const matches: typeof relatedBRs = [];
    // one match more than shown tells us there are further results, without scanning the whole list
    for (const { option, ...searchable } of searchableBrs) {
      if (matchesContainsSearch(suggestion, searchable)) {
        matches.push(option);
        if (matches.length > MAX_BR_OPTIONS) {
          break;
        }
      }
    }
    return { options: matches.slice(0, MAX_BR_OPTIONS), hasMore: matches.length > MAX_BR_OPTIONS };
  }, [searchableBrs, deferredRelatedBrSuggestion]);

  const isRelatedBrDisabled = useMemo(() => {
    if (!isPartnerBR) {
      if (values.relatedBRs.length > 0) {
        return false;
      }
      return values.relatedPartners.length === 0;
    }
    return false;
  }, [isPartnerBR, values.relatedPartners, values.relatedBRs]);

  const defaultBrLabel = useMemo(() => {
    if (usedInBr && brDetails) {
      return formatBrLabel(brDetails.brNumber, brDetails.firstDisplayName);
    }
    return null;
  }, [usedInBr, brDetails]);

  const showAdditionalRelatedBR = useMemo(() => {
    if (
      !usedInBr &&
      defaultBrLabel &&
      edit &&
      values.relatedBRs.length > 0 &&
      (shownNote?.relatedBRs?.length ?? 0) > 0
    ) {
      return values.relatedBRs
        .filter((item) => item !== brDetails?.brUid)
        .map((item) => {
          const relatedBr = shownNote?.relatedBRs.find((co) => co.relatedBrUid === item);
          return (
            <ZfPill size="medium" key={item} style={{ ...GROWING_PILL_STYLE, maxWidth: '100%' }}>
              {formatBrLabel(relatedBr?.masterNumber, relatedBr?.brName)}
            </ZfPill>
          );
        });
    }

    return [];
  }, [usedInBr, defaultBrLabel, edit, values.relatedBRs, brDetails, shownNote?.relatedBRs]);

  useEffect(() => {
    if (defaultBrLabel && !edit && values.relatedBRs.length === 0) {
      setFieldValue('relatedBRs', [brDetails?.brUid]);
    }
  }, [edit, defaultBrLabel]);

  const clientRepShowOther = useMemo(() => {
    if (clientRepresentativeSuggestion !== '') {
      return true;
    }
    if ((clientRepDetailsSuccess || clientRepDetailsFail) && clientRepFilteredOptions.length === 0) {
      return true;
    }
    return false;
  }, [clientRepresentativeSuggestion, clientRepDetailsSuccess, clientRepDetailsFail, clientRepFilteredOptions]);

  const otherClientRepSelected = useMemo(() => {
    return values.relatedPartners.map((item) => item.value).includes('other');
  }, [values.relatedPartners]);

  const otherPartnerMandatoryCheck = useMemo(() => {
    return values.relatedPartners?.map((item) => item.value)?.includes('other');
  }, [values.relatedPartners]);

  const showClientRepTable = useMemo(() => {
    return EnvConfig.config.enableBrClientRepresentativeTable && usedInBr;
  }, [usedInBr, EnvConfig.config.enableBrClientRepresentativeTable]);

  return (
    <>
      <ZfStack direction="row" spacing="small" style={{ paddingBottom: 'var(--gap-vertical-md)' }}>
        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
          {t('contactNote.form.attendeeAndBR')}
        </ZfTypography>
        <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-sm)' }} />
      </ZfStack>
      {showSelectedClientRep && (
        <ZfFormField style={{ flex: 1, minHeight: 78 }} label={t('contactNote.form.selectedClientRepresentative')}>
          <ZfPill size="medium">{selectedClientRepLabel}</ZfPill>
        </ZfFormField>
      )}
      {showClientRepTable && (
        <BRPartnerDetailTable
          nonEditable={nonEditable}
          isFederated={isFederated}
          brUId={brUId}
          partnerUId={partnerUId}
        />
      )}
      {!showClientRepTable && (
        <ZfFormField
          style={{ flex: 1, minHeight: 78 }}
          label={t('contactNote.form.clientRepresentative')}
          alerts={[errors.relatedPartners?.toString() ?? '']}
          mandatory
        >
          {' '}
          <ZfInput
            data-testid="relatedPartners"
            name="relatedPartners"
            disabled={nonEditable}
            type="combo-box"
            onSelectChange={(e) => {
              const selectedList = e.detail;
              const storedSet = new Set(clientRepresentativeList.map((item) => item.value));
              const uniqueItems = (selectedList as string[]).filter((item) => !storedSet.has(item));

              let uniqueList = uniqueItems.map((item) => {
                const selectedItem = clientRepDetails?.hits.find((co) => co.uuid === item);
                const label = `${selectedItem?.name} (${selectedItem?.bizKey})`;
                return { value: item, label, type: selectedItem?.type };
              });

              let uniqueListWithoutType = uniqueList.filter((item) => !item.type);
              uniqueList = uniqueList.filter((item) => item.type);
              //pass the types for edit flow existing related partners
              if (edit && (shownNote?.relatedPartners.length ?? 0) > 0 && uniqueListWithoutType.length > 0) {
                uniqueListWithoutType = uniqueListWithoutType.map((option) => {
                  const selectedObj = shownNote?.relatedPartners.find((co) => co.partnerUId === option.value);
                  const selectedRelatedPartners = values.relatedPartners.find((co) => co.value === option.value);
                  if (selectedObj && selectedRelatedPartners) {
                    return {
                      value: option.value,
                      label: `${selectedObj.fullName} (${selectedObj.globalPartnerId})`,
                      type: selectedRelatedPartners?.type,
                    };
                  }
                  return option;
                });
              }
              const mergeList = [...clientRepresentativeList, ...uniqueList, ...uniqueListWithoutType];
              setClientRepresentativeList(mergeList);
              const updatedList = (selectedList as string[]).map((item: string) =>
                mergeList.find((obj) => obj.value === item),
              );
              if (showSelectedClientRep) {
                const clientRepItem = mergeList.find((obj) => obj.value === techId);
                if (clientRepItem) {
                  updatedList.unshift(clientRepItem);
                }
              }
              if (
                values.relatedPartners?.map((item) => item.value)?.includes('other') &&
                !updatedList.some((item) => item?.value === 'other')
              ) {
                setFieldValue('otherPartner', '');
              }
              setFieldValue('relatedPartners', updatedList);
            }}
            value={relatedPartnersValue}
            allowSearch
            onInputDropdownOpen={() => setClientRepresentativeSuggestion('')}
            onInputChange={(e) => setClientRepresentativeSuggestion(e.detail)}
            placeholder={t('contactNote.form.placeholder.selectOrEnter', {
              name: t('contactNote.form.placeholder.label.clientRepresentative'),
            })}
            error={!!(touched.relatedPartners && errors.relatedPartners)}
            getOptionLabel={(option) => {
              if (includePartnerIntoClientRep && values.partner.partnerUId === option) {
                return `${values.partner.fullName} (${values.partner.globalPartnerId})`;
              }
              if (edit && (shownNote?.relatedPartners.length ?? 0) > 0) {
                const selectedObj = shownNote?.relatedPartners.find((co) => co.partnerUId === option);
                if (selectedObj) {
                  return `${selectedObj.fullName} (${selectedObj.globalPartnerId})`;
                }
              }

              const selectedItem = clientRepresentativeList?.find((co) => co.value === option);
              return option === 'other' ? 'Other' : selectedItem?.label || '';
            }}
            isOptionDisabled={(option) => !usedInBr && option === techId}
          >
            {clientRepresentativeSuggestion === '' && (
              <ZfBox
                style={{
                  padding: 'var(--gap-vertical-xs)',
                  paddingBottom: 'var(--gap-vertical-xs)',
                  paddingLeft: 'var(--gap-horizontal-md)',
                }}
              >
                <ZfTypography variant="body-small" color="normal">
                  {t('contactNote.form.startSearching')}
                </ZfTypography>
              </ZfBox>
            )}
            {clientRepresentativeSuggestion !== '' &&
              (clientRepDetailsSuccess || clientRepDetailsFail) &&
              clientRepFilteredOptions.length === 0 && (
                <ZfBox
                  style={{
                    padding: 'var(--gap-vertical-xs)',
                    paddingBottom: 'var(--gap-vertical-xs)',
                    paddingLeft: 'var(--gap-horizontal-md)',
                  }}
                >
                  <ZfTypography variant="body-small" color="normal">
                    {t('contactNote.form.searchFail')}
                  </ZfTypography>
                </ZfBox>
              )}
            {clientRepDetailsLoading && (
              <ZfLoadingSpinner slot="trailing" size="extra-small" color="primary" type="infinite"></ZfLoadingSpinner>
            )}
            {(clientRepDetailsSuccess || clientRepDetailsFail) && (
              <FixedSizeList
                itemSize={DROPDOWN_HEIGHT}
                itemCount={clientRepFilteredOptions.length}
                overscanCount={5}
                height={Math.min(clientRepFilteredOptions.length, 10) * DROPDOWN_HEIGHT}
                width="100%"
              >
                {(props: ListChildComponentProps) => {
                  const { index, style } = props;
                  const co = clientRepFilteredOptions[index];

                  return (
                    <ZfSelectOption
                      style={style}
                      key={co.value}
                      value={co.value}
                      disabled={edit && co.value === techId}
                      isSelected={values.relatedPartners.map((item) => item.value).includes(co.value)}
                    >
                      {co.label}
                    </ZfSelectOption>
                  );
                }}
              </FixedSizeList>
            )}
            {clientRepShowOther && (
              <ZfSelectOption value="other" key="other" isSelected={otherClientRepSelected}>
                {t('contactNote.form.other')}
              </ZfSelectOption>
            )}
          </ZfInput>
          <ZfPopover slot="label-trailing" alignment="start" data-testid="client-rep-popover">
            <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
            <ZfBox styles={{ textWrap: 'wrap' }}>
              <ZfTypography variant="label-tiny" color="normal">
                {t('contactNote.form.clientRepresentativePopover')}
              </ZfTypography>
            </ZfBox>
          </ZfPopover>
        </ZfFormField>
      )}
      <ZfFormField
        style={{ flex: 1, minHeight: 78 }}
        label={t('contactNote.form.otherPartner')}
        alerts={[errors.otherPartner ?? '']}
        mandatory={otherPartnerMandatoryCheck}
      >
        <ZfInput
          {...getFieldProps('otherPartner')}
          placeholder={t('contactNote.form.placeholder.otherPartner')}
          disabled={nonEditable}
          counter
          error={!!errors.otherPartner && touched.otherPartner}
          maxLength={200}
          onInputChange={getFieldProps('otherPartner').onChange}
          name="otherPartner"
          data-testid="otherPartner"
        />
      </ZfFormField>
      <ZfFormField
        style={{ flex: 1, minHeight: 78 }}
        label={t('contactNote.form.relatedBRs')}
        mandatory={isPartner}
        alerts={[errors.relatedBRs?.toString() ?? '']}
      >
        {!usedInBr && (
          <ZfInput
            placeholder={t('contactNote.form.placeholder.selectOrEnter', {
              name: t('contactNote.form.placeholder.label.relatedBRs'),
            })}
            name="relatedBRs"
            data-testid="relatedBRs"
            style={GROWING_PILL_STYLE}
            type="combo-box"
            onSelectChange={(e) => {
              const selectedList = e.detail;
              if (!isPartnerBR) {
                const storedSet = new Set(relatedBrList.map((item) => item.value));
                const uniqueItems = (selectedList as string[]).filter((item) => !storedSet.has(item));

                const uniqueList = uniqueItems.map((item) => {
                  const selectedItem = relatedBRs.find((co) => co.value === item);
                  return { value: item, label: selectedItem?.label || '' };
                });

                const mergeList = [...relatedBrList, ...uniqueList];
                setRelatedBrList(mergeList);
              }
              setFieldValue('relatedBRs', selectedList);
            }}
            allowSearch
            disabled={nonEditable || isRelatedBrDisabled}
            onInputDropdownOpen={() => setRelatedBrSuggestion('')}
            onInputChange={(e) => setRelatedBrSuggestion(e.detail.replaceAll('.', ''))}
            error={!!(touched.relatedBRs && errors.relatedBRs)}
            getOptionLabel={(option) => {
              if (edit) {
                const selectedBr = shownNote?.relatedBRs.find((item) => item.relatedBrUid === option);
                if (selectedBr) {
                  return formatBrLabel(selectedBr.masterNumber, selectedBr.brName);
                }
              }
              if (isPartnerBR) {
                return relatedBRs.find((co) => co.value === option)?.label || '';
              } else {
                return relatedBrList.find((co) => co.value === option)?.label || '';
              }
            }}
            value={values.relatedBRs}
          >
            {!relatedBrloading && relatedBRFilteredOption.length === 0 && (
              <ZfBox
                style={{
                  padding: 'var(--gap-vertical-xs)',
                  paddingBottom: 'var(--gap-vertical-xs)',
                  paddingLeft: 'var(--gap-horizontal-md)',
                }}
              >
                <ZfTypography variant="body-small" color="normal">
                  {t('contactNote.form.searchFail')}
                </ZfTypography>
              </ZfBox>
            )}
            {relatedBrloading && (
              <ZfLoadingSpinner slot="trailing" size="extra-small" color="primary" type="infinite"></ZfLoadingSpinner>
            )}
            {!relatedBrloading && (
              <FixedSizeList
                itemSize={DROPDOWN_HEIGHT}
                itemCount={relatedBRFilteredOption.length}
                overscanCount={5}
                height={Math.min(relatedBRFilteredOption.length, 10) * DROPDOWN_HEIGHT}
                width="100%"
              >
                {(props: ListChildComponentProps) => {
                  const { index, style } = props;
                  const co = relatedBRFilteredOption[index];

                  return (
                    <ZfSelectOption
                      style={style}
                      key={co.value}
                      value={co.value}
                      isSelected={values.relatedBRs.includes(co.value)}
                    >
                      {co.label}
                    </ZfSelectOption>
                  );
                }}
              </FixedSizeList>
            )}
            {!relatedBrloading && hasMoreRelatedBrOptions && (
              <ZfBox
                style={{
                  padding: 'var(--gap-vertical-xs)',
                  paddingBottom: 'var(--gap-vertical-xs)',
                  paddingLeft: 'var(--gap-horizontal-md)',
                }}
              >
                <ZfTypography variant="body-small" color="normal">
                  {t('contactNote.form.moreResults', { count: MAX_BR_OPTIONS })}
                </ZfTypography>
              </ZfBox>
            )}
          </ZfInput>
        )}
        {defaultBrLabel && (
          <ZfStack direction="row" spacing="small" style={{ minWidth: 0, flexWrap: 'wrap' }}>
            <ZfPill size="medium" style={{ ...GROWING_PILL_STYLE, maxWidth: '100%' }}>
              {defaultBrLabel}
            </ZfPill>
            {showAdditionalRelatedBR}
          </ZfStack>
        )}

        <ZfPopover slot="label-trailing" alignment="start" data-testid="related-br-popover">
          <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
          <ZfBox>
            <ZfTypography variant="label-tiny" color="normal">
              {t('contactNote.form.relatedBRsPopover')}
            </ZfTypography>
          </ZfBox>
        </ZfPopover>
      </ZfFormField>
      <ZfFormField
        style={{ flex: 1, minHeight: 78 }}
        label={t('contactNote.form.bankRepresentative')}
        mandatory
        alerts={[errors.additionalAttendees?.toString() ?? '']}
      >
        <ZfInput
          name="additionalAttendees"
          type="combo-box"
          data-testid="additionalAttendees"
          allowSearch
          placeholder={t('contactNote.form.placeholder.selectOrEnter', {
            name: t('contactNote.form.placeholder.label.bankRepresentative'),
          })}
          onSelectChange={getFieldProps('additionalAttendees').onChange}
          onInputDropdownOpen={() => setEmployeeSuggestion('')}
          onInputChange={(e) => setEmployeeSuggestion(e.detail)}
          disabled={nonEditable || myTravelNonEditableField}
          getOptionLabel={(option) =>
            employeeOptionsWithInactiveUsers.find((co) => co.value === option.toLowerCase())?.label || `N/A (${option})`
          }
          error={!!(touched.additionalAttendees && errors.additionalAttendees)}
          value={values.additionalAttendees}
        >
          {employeeSuggestion !== '' && employeeFilteredOptions.length === 0 && (
            <ZfBox
              style={{
                padding: 'var(--gap-vertical-xs)',
                paddingBottom: 'var(--gap-vertical-xs)',
                paddingLeft: 'var(--gap-horizontal-md)',
              }}
            >
              <ZfTypography variant="body-small" color="normal">
                {t('contactNote.form.searchFail')}
              </ZfTypography>
            </ZfBox>
          )}
          {!relatedBrloading && (
            <FixedSizeList
              itemSize={DROPDOWN_HEIGHT}
              itemCount={employeeFilteredOptions.length}
              overscanCount={5}
              height={Math.min(employeeFilteredOptions.length, 10) * DROPDOWN_HEIGHT}
              width="100%"
            >
              {(props: ListChildComponentProps) => {
                const { index, style } = props;
                const co = employeeFilteredOptions[index];

                return (
                  <ZfSelectOption
                    style={style}
                    key={co.value}
                    value={co.value}
                    isSelected={values.additionalAttendees.includes(co.value)}
                  >
                    {co.label}
                  </ZfSelectOption>
                );
              }}
            </FixedSizeList>
          )}
        </ZfInput>
      </ZfFormField>
    </>
  );
};

export default AttendeesBr;
